export type LockOutcome =
  // Ran to completion, nothing queued behind it.
  | 'ran'
  // Turned away because a task was in flight; a re-run was queued. The caller must NOT signal —
  // it returns before the running task settles, so signalling here would loop.
  | 'coalesced'
  // Ran to completion, and a request queued behind it. The caller signals a re-run.
  | 'drain-requested';

export type CoalescingLock = Readonly<{
  run: (task: () => Promise<void>) => Promise<LockOutcome>;
}>;

/**
 * Serializes an async task, coalescing every request that arrives while one is in flight into a
 * single follow-up.
 *
 * The lock only ever *signals* that a re-run is wanted; it never performs one. Re-invoking the
 * queued task closure would replay whatever inputs it captured, and callers whose inputs live in
 * a render need to re-read them fresh and re-evaluate their own guard before deciding a re-run
 * is warranted at all. Replay semantics would skip that guard.
 */
export const createCoalescingLock = (): CoalescingLock => {
  let isRunning = false;
  let hasQueued = false;

  return {
    run: async (task: () => Promise<void>): Promise<LockOutcome> => {
      if (isRunning) {
        hasQueued = true;
        return 'coalesced';
      }

      // Set before the first await, so concurrent synchronous callers — React 18 StrictMode's
      // dev double-invoke among them — coalesce rather than both running.
      isRunning = true;
      try {
        await task();
      } finally {
        isRunning = false;
      }

      if (hasQueued) {
        hasQueued = false;
        return 'drain-requested';
      }
      return 'ran';
    },
  };
};
